import { DATA_CONTENT_EDITABLE_IDX, useBlock } from '@bay-email/editor';
import { AdvancedType, t } from '@bay-email/core';
import React, { useMemo } from 'react';
import { get } from 'lodash';
import { RteColorPicker as NativeColorPicker } from '@extensions/components/Form/RteColorPicker';
import { ToolItem } from '../../ToolItem';
import { BsPaintBucket } from 'react-icons/bs';
import { useTiptapEditor } from '../TiptapEditorContext';

interface BrandColor {
  label: string;
  color: string;
  value: string;
}

function getTdAncestor(range: Range | null): HTMLTableCellElement | null {
  if (!range) return null;
  let node: Node | null = range.commonAncestorContainer;
  while (node) {
    if (node.nodeName === 'TD') return node as HTMLTableCellElement;
    node = node.parentNode;
  }
  return null;
}

export function TableCellBgColor({
  selectionRange,
  getPopoverMountNode,
  brandColors,
}: {
  selectionRange: Range | null;
  getPopoverMountNode: () => HTMLElement;
  brandColors?: BrandColor[];
}) {
  const { focusBlock, change, values } = useBlock();

  const editor = useTiptapEditor();

  // With the tiptap editor the cell is the editor's own ancestor, so it stays
  // right even when the selection range cannot be mapped to the DOM.
  const td = useMemo(
    () =>
      editor
        ? (editor.view.dom.closest('td') as HTMLTableCellElement | null)
        : getTdAncestor(selectionRange),
    [editor, selectionRange],
  );

  // The cell's stored backgroundColor field path (e.g. "...tableSource.0.1.backgroundColor").
  const cellBgPath = useMemo(() => {
    const attr = td?.getAttribute(DATA_CONTENT_EDITABLE_IDX);
    const match = attr?.match(/^(.+\.tableSource\.\d+\.\d+)\.content$/);
    return match ? `${match[1]}.backgroundColor` : null;
  }, [td]);

  // Derive the displayed value directly from the model each render (like the other RTE
  // colour tools) rather than mirroring it in local state — a local mirror lags the
  // model round-trip by a render or two, which is why applied colours took a few cycles
  // to show. Prefer the stored model value (which may be a brand merge tag, so the
  // picker re-opens in Dynamic mode); fall back to the rendered inline colour.
  const color = (cellBgPath ? (get(values, cellBgPath) as string | undefined) : undefined)
    || td?.style.backgroundColor
    || undefined;

  if (focusBlock?.type !== AdvancedType.TABLE || !td || !cellBgPath) return null;

  // Store the chosen value on the cell. A literal hex stores as-is; a brand selection
  // stores its merge tag (e.g. "{{user.brandPrimaryColor}}"), which the table render
  // resolves to the real hex on the canvas and keeps as a tag at send time.
  //
  // The editor canvas freezes block re-renders while a cell is focused (to protect the
  // caret/selection), so a model-only write isn't reflected until blur. Paint the live
  // <td> immediately too — with the resolved hex for a brand tag — for instant feedback,
  // mirroring how the foreColor/hiliteColor tools mutate the DOM directly.
  const applyValue = (nextValue: string) => {
    change(cellBgPath, nextValue);
    const visibleColor = brandColors?.find(b => b.value === nextValue)?.color ?? nextValue;
    td.style.backgroundColor = visibleColor;
  };

  // The underline swatch needs a real colour; when the value is a brand merge tag,
  // show the brand's resolved hex instead of the (uncolourable) tag string.
  const displayColor =
    brandColors?.find(b => b.value === color)?.color ?? color ?? 'transparent';

  return (
    <NativeColorPicker
      value={color}
      brandColors={brandColors}
      supportsDynamic
      onChange={applyValue}
      // Static colours commit on release via onCommit; without handling it the picker
      // can close/rebuild before the onChange write settles and the colour is dropped.
      onCommit={applyValue}
      onDynamicCommit={brandValue => applyValue(brandValue)}
      getPopupContainer={getPopoverMountNode}
      highlightRange={selectionRange}
      showInput={false}
    >
      <ToolItem
        icon={(
          <div style={{ position: 'relative' }}>
            <BsPaintBucket size={13} style={{ position: 'relative', top: '-1px' }} />
            <div style={{ borderBottom: `2px solid ${displayColor}`, position: 'absolute', width: '130%', left: '-15%', top: 18 }} />
          </div>
        )}
        title={t('Cell Background Color')}
      />
    </NativeColorPicker>
  );
}
